import type { Schemas } from '@ers/sdk';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call, ifMatch } from '../../api/client';
import type { ApiBody, ApiQuery, ApiResponse } from '../../components/data/apiTypes';
import { useCursorQuery } from '../../components/data/useCursorQuery';
import { useRouteProbe } from '../../components/data/useRouteProbe';

/*
 * Модуль «Персонал» (М12): профілі й посади, адаптація, реєстр порушень і «градієнт впливу», оцінка
 * відповідності посаді, мотивація й бонуси, завершення співпраці, маршрути погодження відсутностей.
 * Типи — лише з контракту (@ers/sdk). Ключі кешу — під префіксом ['people']: подія потоку «people.*»
 * оновлює модуль цілком (useLiveInvalidate у каркасі модуля).
 */

// ——— Типи з контракту ———

export type Profile = Schemas['PeopleProfile'];
export type Position = Schemas['PeoplePosition'];
export type Assignment = Schemas['PeoplePositionAssignment'];
export type GradeRecord = Schemas['PeopleGradeRecord'];
export type PersonalData = Schemas['PeoplePersonalData'];
export type PeopleSettings = Schemas['PeopleSettings'];
export type AdaptationTemplate = Schemas['PeopleAdaptationTemplate'];
export type AdaptationItem = Schemas['PeopleAdaptationItem'];
export type Adaptation = Schemas['PeopleAdaptation'];
export type Checkpoint = Schemas['PeopleAdaptationCheckpoint'];
export type Violation = Schemas['PeopleViolation'];
export type InfluenceStep = Schemas['PeopleInfluenceStep'];
export type InfluenceAction = Schemas['PeopleInfluenceAction'];
export type MetricSource = Schemas['PeopleMetricSource'];
export type Criterion = Schemas['PeopleAssessmentCriterion'];
export type Assessment = Schemas['PeopleAssessment'];
export type MetricValue = Schemas['PeopleMetricValue'];
export type CriterionScore = Schemas['PeopleCriterionScore'];
export type BonusScheme = Schemas['PeopleBonusScheme'];
export type BonusInput = Schemas['PeopleBonusInput'];
export type BonusStep = Schemas['PeopleBonusStep'];
export type BonusParam = Schemas['PeopleBonusParam'];
export type BonusCalculation = Schemas['PeopleBonusCalculation'];
export type BonusPreview = Schemas['PeopleBonusPreview'];
export type Offboarding = Schemas['PeopleOffboarding'];
export type OffboardingItem = Schemas['PeopleOffboardingItem'];
export type AbsenceRoute = Schemas['PeopleAbsenceRoute'];
export type ViolationStats = ApiResponse<'/api/v1/people/violations/stats', 'get'>;
export type KbFolder = ApiResponse<'/api/v1/kb/users/{id}/folder', 'get'>;
export type KbTrainingLog = ApiResponse<'/api/v1/kb/training-log', 'get'>;
export type KbAdmission = Schemas['KbAdmission'];
export type KbInspection = Schemas['KbInspection'];
export type TimeAbsenceType = Schemas['TimeAbsenceType'];

export type ProfilePatch = ApiBody<'/api/v1/people/profiles/{userId}', 'patch'>;
export type PersonalDataBody = ApiBody<'/api/v1/people/profiles/{userId}/personal-data', 'put'>;
export type PositionCreate = ApiBody<'/api/v1/people/positions', 'post'>;
export type PositionPatch = ApiBody<'/api/v1/people/positions/{id}', 'patch'>;
export type AssignBody = ApiBody<'/api/v1/people/profiles/{userId}/positions', 'post'>;
export type GradeBody = ApiBody<'/api/v1/people/profiles/{userId}/grades', 'post'>;
export type SettingsPatch = ApiBody<'/api/v1/people/settings', 'patch'>;
export type TemplateCreate = ApiBody<'/api/v1/people/adaptation-templates', 'post'>;
export type TemplatePatch = ApiBody<'/api/v1/people/adaptation-templates/{id}', 'patch'>;
export type AdaptationCreate = ApiBody<'/api/v1/people/adaptations', 'post'>;
export type CheckpointBody = ApiBody<'/api/v1/people/adaptations/{id}/checkpoints/{checkpointId}', 'post'>;
export type TestDayBody = ApiBody<'/api/v1/people/adaptations/{id}/test-day', 'post'>;
export type AdmissionBody = ApiBody<'/api/v1/people/adaptations/{id}/decision', 'post'>;
export type ViolationCreate = ApiBody<'/api/v1/people/violations', 'post'>;
export type ViolationDecisionBody = ApiBody<'/api/v1/people/violations/{id}/decision', 'post'>;
export type AppealDecisionBody = ApiBody<'/api/v1/people/violations/{id}/appeal-decision', 'post'>;
export type StepCreate = ApiBody<'/api/v1/people/influence-steps', 'post'>;
export type StepPatch = ApiBody<'/api/v1/people/influence-steps/{id}', 'patch'>;
export type InfluenceCreate = ApiBody<'/api/v1/people/influence-actions', 'post'>;
export type CriterionCreate = ApiBody<'/api/v1/people/assessment-criteria', 'post'>;
export type CriterionPatch = ApiBody<'/api/v1/people/assessment-criteria/{id}', 'patch'>;
export type AssessmentCreate = ApiBody<'/api/v1/people/assessments', 'post'>;
export type AssessmentComplete = ApiBody<'/api/v1/people/assessments/{id}/complete', 'post'>;
export type SchemeCreate = ApiBody<'/api/v1/people/bonus-schemes', 'post'>;
export type SchemePatch = ApiBody<'/api/v1/people/bonus-schemes/{id}', 'patch'>;
export type PreviewBody = ApiBody<'/api/v1/people/bonus-schemes/{id}/preview', 'post'>;
export type ParamCreate = ApiBody<'/api/v1/people/bonus-params', 'post'>;
export type ParamPatch = ApiBody<'/api/v1/people/bonus-params/{id}', 'patch'>;
export type CalcCreate = ApiBody<'/api/v1/people/bonus-calculations', 'post'>;
export type OffboardingCreate = ApiBody<'/api/v1/people/offboardings', 'post'>;
export type ConfirmItemBody = ApiBody<'/api/v1/people/offboardings/{id}/confirm', 'post'>;
export type AbsenceDecideBody = ApiBody<'/api/v1/people/absence-routes/{id}/decide', 'post'>;
export type AbsenceOrderBody = ApiBody<'/api/v1/people/absence-routes/{id}/order', 'post'>;

export type ProfilesQuery = ApiQuery<'/api/v1/people/profiles', 'get'>;
export type AdaptationsQuery = Omit<ApiQuery<'/api/v1/people/adaptations', 'get'>, 'cursor' | 'limit'>;
export type ViolationsQuery = Omit<ApiQuery<'/api/v1/people/violations', 'get'>, 'cursor' | 'limit'>;
export type ActionsQuery = Omit<ApiQuery<'/api/v1/people/influence-actions', 'get'>, 'cursor' | 'limit'>;
export type AssessmentsQuery = Omit<ApiQuery<'/api/v1/people/assessments', 'get'>, 'cursor' | 'limit'>;
export type CalcsQuery = Omit<ApiQuery<'/api/v1/people/bonus-calculations', 'get'>, 'cursor' | 'limit'>;
export type ParamsQuery = ApiQuery<'/api/v1/people/bonus-params', 'get'>;
export type OffboardingsQuery = Omit<ApiQuery<'/api/v1/people/offboardings', 'get'>, 'cursor' | 'limit'>;
export type AbsenceRoutesQuery = Omit<ApiQuery<'/api/v1/people/absence-routes', 'get'>, 'cursor' | 'limit'>;

// ——— Ключі кешу ———

type Q = Record<string, unknown>;

export const peopleKeys = {
  all: ['people'] as const,
  probe: ['people', 'probe'] as const,
  positions: (includeArchived: boolean) => ['people', 'positions', { includeArchived }] as const,
  profiles: (q: Q) => ['people', 'profiles', q] as const,
  profile: (id: string) => ['people', 'profile', id] as const,
  personal: (id: string) => ['people', 'personal', id] as const,
  assignments: (id: string) => ['people', 'assignments', id] as const,
  grades: (id: string) => ['people', 'grades', id] as const,
  settings: ['people', 'settings'] as const,
  templates: ['people', 'adaptation-templates'] as const,
  adaptations: (q: Q) => ['people', 'adaptations', q] as const,
  adaptation: (id: string) => ['people', 'adaptation', id] as const,
  violations: (q: Q) => ['people', 'violations', q] as const,
  violation: (id: string) => ['people', 'violation', id] as const,
  violationStats: (month: string) => ['people', 'violation-stats', month] as const,
  steps: ['people', 'influence-steps'] as const,
  actions: (q: Q) => ['people', 'influence-actions', q] as const,
  metricSources: ['people', 'metric-sources'] as const,
  criteria: ['people', 'assessment-criteria'] as const,
  assessments: (q: Q) => ['people', 'assessments', q] as const,
  assessment: (id: string) => ['people', 'assessment', id] as const,
  schemes: ['people', 'bonus-schemes'] as const,
  params: (q: Q) => ['people', 'bonus-params', q] as const,
  calcs: (q: Q) => ['people', 'bonus-calcs', q] as const,
  calc: (id: string) => ['people', 'bonus-calc', id] as const,
  offboardings: (q: Q) => ['people', 'offboardings', q] as const,
  offboarding: (id: string) => ['people', 'offboarding', id] as const,
  absenceRoutes: (q: Q) => ['people', 'absence-routes', q] as const,
  absenceRoute: (id: string) => ['people', 'absence-route', id] as const,
  kbFolder: (id: string) => ['people', 'kb', 'folder', id] as const,
  kbLog: (id: string) => ['people', 'kb', 'log', id] as const,
  kbAdmissions: (id: string) => ['people', 'kb', 'admissions', id] as const,
  kbInspections: (id: string) => ['people', 'kb', 'inspections', id] as const,
  absenceTypes: ['people', 'absence-types'] as const,
};

/** Прибрати порожні значення з фільтрів (щоб ключ кешу й запит були короткими). */
function clean<T extends Q>(q: T): T {
  const out: Q = {};
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') out[k] = v;
  return out as T;
}

/** Доступ до модуля: сервер відповідає на довідник посад (доступний усім працівникам). */
export function usePeopleAvailable(): boolean {
  return useRouteProbe(peopleKeys.probe, '/api/v1/people/positions').available;
}

// ——— Довідники ———

export function usePositions(includeArchived = false) {
  return useQuery({
    queryKey: peopleKeys.positions(includeArchived),
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/people/positions', { params: { query: includeArchived ? { includeArchived: true } : {} } }))).data,
  });
}

export function useSettings(enabled = true) {
  return useQuery({
    queryKey: peopleKeys.settings,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () => call(api.GET('/api/v1/people/settings')),
  });
}

export function useTemplates(enabled = true) {
  return useQuery({
    queryKey: peopleKeys.templates,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/people/adaptation-templates'))).data,
  });
}

export function useInfluenceSteps() {
  return useQuery({
    queryKey: peopleKeys.steps,
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/people/influence-steps'))).data,
  });
}

export function useMetricSources(enabled = true) {
  return useQuery({
    queryKey: peopleKeys.metricSources,
    enabled,
    staleTime: 10 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/people/metric-sources'))).data,
  });
}

export function useCriteria(enabled = true) {
  return useQuery({
    queryKey: peopleKeys.criteria,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/people/assessment-criteria'))).data,
  });
}

export function useSchemes(enabled = true) {
  return useQuery({
    queryKey: peopleKeys.schemes,
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => (await call(api.GET('/api/v1/people/bonus-schemes'))).data,
  });
}

/** Типи відсутностей модуля «Облік часу» (для маршруту погодження); без модуля — порожньо. */
export function useAbsenceTypes(enabled = true) {
  return useQuery({
    queryKey: peopleKeys.absenceTypes,
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/time/absence-types'))).data,
  });
}

// ——— Профілі ———

export function useProfiles(q: ProfilesQuery, enabled = true) {
  const query = clean(q);
  return useQuery({
    queryKey: peopleKeys.profiles(query),
    enabled,
    staleTime: 30_000,
    queryFn: async () => (await call(api.GET('/api/v1/people/profiles', { params: { query } }))).data,
  });
}

export function useProfile(userId: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.profile(userId ?? ''),
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: () => call(api.GET('/api/v1/people/profiles/{userId}', { params: { path: { userId: userId! } } })),
  });
}

export function usePersonalData(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: peopleKeys.personal(userId),
    enabled,
    // Кожен перегляд HR пишеться в журнал аудиту — не перезапитувати без потреби.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: () => call(api.GET('/api/v1/people/profiles/{userId}/personal-data', { params: { path: { userId } } })),
  });
}

export function useAssignments(userId: string) {
  return useQuery({
    queryKey: peopleKeys.assignments(userId),
    queryFn: async () => (await call(api.GET('/api/v1/people/profiles/{userId}/positions', { params: { path: { userId } } }))).data,
  });
}

export function useGrades(userId: string) {
  return useQuery({
    queryKey: peopleKeys.grades(userId),
    queryFn: async () => (await call(api.GET('/api/v1/people/profiles/{userId}/grades', { params: { path: { userId } } }))).data,
  });
}

// ——— База знань (посадова папка, навчання) — тиха деградація, якщо модуль не підключено ———

export function useKbFolder(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: peopleKeys.kbFolder(userId),
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: () => call(api.GET('/api/v1/kb/users/{id}/folder', { params: { path: { id: userId } } })),
  });
}

export function useKbTrainingLog(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: peopleKeys.kbLog(userId),
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/kb/training-log', { params: { query: { userId, limit: 100 } } }))).data,
  });
}

export function useKbAdmissions(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: peopleKeys.kbAdmissions(userId),
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/kb/admissions', { params: { query: { userId } } }))).data,
  });
}

export function useKbInspections(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: peopleKeys.kbInspections(userId),
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/kb/inspections', { params: { query: { userId, limit: 50 } } }))).data,
  });
}

// ——— Переліки з курсорною пагінацією ———

export function useAdaptationList(q: AdaptationsQuery, enabled = true) {
  const query = clean(q);
  return useCursorQuery(
    peopleKeys.adaptations(query),
    (cursor) => call(api.GET('/api/v1/people/adaptations', { params: { query: { ...query, cursor, limit: 50 } } })),
    { enabled },
  );
}

export function useAdaptation(id: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.adaptation(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/people/adaptations/{id}', { params: { path: { id: id! } } })),
  });
}

export function useViolationList(q: ViolationsQuery, enabled = true) {
  const query = clean(q);
  return useCursorQuery(
    peopleKeys.violations(query),
    (cursor) => call(api.GET('/api/v1/people/violations', { params: { query: { ...query, cursor, limit: 50 } } })),
    { enabled },
  );
}

export function useViolation(id: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.violation(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/people/violations/{id}', { params: { path: { id: id! } } })),
  });
}

export function useViolationStats(month: string) {
  return useQuery({
    queryKey: peopleKeys.violationStats(month),
    queryFn: () => call(api.GET('/api/v1/people/violations/stats', { params: { query: { month } } })),
  });
}

export function useInfluenceActions(q: ActionsQuery) {
  const query = clean(q);
  return useCursorQuery(peopleKeys.actions(query), (cursor) => call(api.GET('/api/v1/people/influence-actions', { params: { query: { ...query, cursor, limit: 50 } } })));
}

export function useAssessmentList(q: AssessmentsQuery) {
  const query = clean(q);
  return useCursorQuery(peopleKeys.assessments(query), (cursor) => call(api.GET('/api/v1/people/assessments', { params: { query: { ...query, cursor, limit: 50 } } })));
}

export function useAssessment(id: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.assessment(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/people/assessments/{id}', { params: { path: { id: id! } } })),
  });
}

export function useBonusParams(q: ParamsQuery, enabled = true) {
  const query = clean(q);
  return useQuery({
    queryKey: peopleKeys.params(query),
    enabled,
    queryFn: async () => (await call(api.GET('/api/v1/people/bonus-params', { params: { query } }))).data,
  });
}

export function useCalcList(q: CalcsQuery) {
  const query = clean(q);
  return useCursorQuery(peopleKeys.calcs(query), (cursor) => call(api.GET('/api/v1/people/bonus-calculations', { params: { query: { ...query, cursor, limit: 50 } } })));
}

export function useCalc(id: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.calc(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/people/bonus-calculations/{id}', { params: { path: { id: id! } } })),
  });
}

export function useOffboardingList(q: OffboardingsQuery, enabled = true) {
  const query = clean(q);
  return useCursorQuery(
    peopleKeys.offboardings(query),
    (cursor) => call(api.GET('/api/v1/people/offboardings', { params: { query: { ...query, cursor, limit: 50 } } })),
    { enabled },
  );
}

export function useOffboarding(id: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.offboarding(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/people/offboardings/{id}', { params: { path: { id: id! } } })),
  });
}

export function useAbsenceRouteList(q: AbsenceRoutesQuery) {
  const query = clean(q);
  return useCursorQuery(peopleKeys.absenceRoutes(query), (cursor) => call(api.GET('/api/v1/people/absence-routes', { params: { query: { ...query, cursor, limit: 50 } } })));
}

export function useAbsenceRoute(id: string | null | undefined) {
  return useQuery({
    queryKey: peopleKeys.absenceRoute(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/people/absence-routes/{id}', { params: { path: { id: id! } } })),
  });
}

// ——— Зміни (після кожної — оновлення модуля цілком) ———

function useMut<V, R>(fn: (v: V) => Promise<R>, after?: (qc: QueryClient, r: R, v: V) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, v) => {
      after?.(qc, r, v);
      void qc.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

// Посади
export const useCreatePosition = () => useMut((body: PositionCreate) => call(api.POST('/api/v1/people/positions', { body })));
export const usePatchPosition = () =>
  useMut(({ id, version, body }: { id: string; version: number; body: PositionPatch }) =>
    call(api.PATCH('/api/v1/people/positions/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
  );

// Профіль
export const usePatchProfile = () =>
  useMut(
    ({ userId, version, body }: { userId: string; version: number; body: ProfilePatch }) =>
      call(api.PATCH('/api/v1/people/profiles/{userId}', { params: { path: { userId } }, body, headers: ifMatch(version) })),
    (qc, p) => qc.setQueryData(peopleKeys.profile(p.userId), p),
  );
export const useSavePersonalData = () =>
  useMut(
    ({ userId, body }: { userId: string; body: PersonalDataBody }) => call(api.PUT('/api/v1/people/profiles/{userId}/personal-data', { params: { path: { userId } }, body })),
    (qc, d, v) => qc.setQueryData(peopleKeys.personal(v.userId), d),
  );
export const useAnonymizePersonalData = () =>
  useMut(
    ({ userId, reason }: { userId: string; reason: string }) => call(api.DELETE('/api/v1/people/profiles/{userId}/personal-data', { params: { path: { userId } }, body: { reason } })),
    (qc, d, v) => qc.setQueryData(peopleKeys.personal(v.userId), d),
  );
export const useAssignPosition = () =>
  useMut(({ userId, body }: { userId: string; body: AssignBody }) => call(api.POST('/api/v1/people/profiles/{userId}/positions', { params: { path: { userId } }, body })), (qc) => {
    // Посада в картці працівника ядра могла змінитися.
    void qc.invalidateQueries({ queryKey: ['users'] });
  });
export const useChangeGrade = () =>
  useMut(({ userId, body }: { userId: string; body: GradeBody }) => call(api.POST('/api/v1/people/profiles/{userId}/grades', { params: { path: { userId } }, body })));

// Налаштування
export const usePatchSettings = () => useMut((body: SettingsPatch) => call(api.PATCH('/api/v1/people/settings', { body })), (qc, s) => qc.setQueryData(peopleKeys.settings, s));
export const useCreateTemplate = () => useMut((body: TemplateCreate) => call(api.POST('/api/v1/people/adaptation-templates', { body })));
export const usePatchTemplate = () =>
  useMut(({ id, body }: { id: string; body: TemplatePatch }) => call(api.PATCH('/api/v1/people/adaptation-templates/{id}', { params: { path: { id } }, body })));
export const useCreateStep = () => useMut((body: StepCreate) => call(api.POST('/api/v1/people/influence-steps', { body })));
export const usePatchStep = () => useMut(({ id, body }: { id: string; body: StepPatch }) => call(api.PATCH('/api/v1/people/influence-steps/{id}', { params: { path: { id } }, body })));
export const useCreateCriterion = () => useMut((body: CriterionCreate) => call(api.POST('/api/v1/people/assessment-criteria', { body })));
export const usePatchCriterion = () =>
  useMut(({ id, body }: { id: string; body: CriterionPatch }) => call(api.PATCH('/api/v1/people/assessment-criteria/{id}', { params: { path: { id } }, body })));

// Адаптація
export const useStartAdaptation = () => useMut((body: AdaptationCreate) => call(api.POST('/api/v1/people/adaptations', { body })));
const setAdaptation = (qc: QueryClient, a: Adaptation) => qc.setQueryData(peopleKeys.adaptation(a.id), a);
export const useRecordCheckpoint = () =>
  useMut(
    ({ id, checkpointId, body }: { id: string; checkpointId: string; body: CheckpointBody }) =>
      call(api.POST('/api/v1/people/adaptations/{id}/checkpoints/{checkpointId}', { params: { path: { id, checkpointId } }, body })),
    setAdaptation,
  );
export const useRecordTestDay = () =>
  useMut(({ id, body }: { id: string; body: TestDayBody }) => call(api.POST('/api/v1/people/adaptations/{id}/test-day', { params: { path: { id } }, body })), setAdaptation);
export const useAdmissionDecision = () =>
  useMut(({ id, body }: { id: string; body: AdmissionBody }) => call(api.POST('/api/v1/people/adaptations/{id}/decision', { params: { path: { id } }, body })), setAdaptation);

// Порушення й «градієнт впливу»
const setViolation = (qc: QueryClient, v: Violation) => qc.setQueryData(peopleKeys.violation(v.id), v);
export const useReportViolation = () => useMut((body: ViolationCreate) => call(api.POST('/api/v1/people/violations', { body })), setViolation);
export const useDecideViolation = () =>
  useMut(({ id, body }: { id: string; body: ViolationDecisionBody }) => call(api.POST('/api/v1/people/violations/{id}/decision', { params: { path: { id } }, body })), setViolation);
export const useAppealViolation = () =>
  useMut(({ id, text }: { id: string; text: string }) => call(api.POST('/api/v1/people/violations/{id}/appeal', { params: { path: { id } }, body: { text } })), setViolation);
export const useDecideAppeal = () =>
  useMut(
    ({ id, body }: { id: string; body: AppealDecisionBody }) => call(api.POST('/api/v1/people/violations/{id}/appeal-decision', { params: { path: { id } }, body })),
    setViolation,
  );
export const useApplyStep = () => useMut((body: InfluenceCreate) => call(api.POST('/api/v1/people/influence-actions', { body })));

// Оцінка відповідності посаді
const setAssessment = (qc: QueryClient, a: Assessment) => qc.setQueryData(peopleKeys.assessment(a.id), a);
export const usePrepareAssessment = () => useMut((body: AssessmentCreate) => call(api.POST('/api/v1/people/assessments', { body })), setAssessment);
export const useRefreshAssessment = () => useMut((id: string) => call(api.POST('/api/v1/people/assessments/{id}/refresh', { params: { path: { id } } })), setAssessment);
export const useCompleteAssessment = () =>
  useMut(({ id, body }: { id: string; body: AssessmentComplete }) => call(api.POST('/api/v1/people/assessments/{id}/complete', { params: { path: { id } }, body })), setAssessment);
export const useAcknowledgeAssessment = () =>
  useMut(
    ({ id, comment }: { id: string; comment?: string }) => call(api.POST('/api/v1/people/assessments/{id}/acknowledge', { params: { path: { id } }, body: comment ? { comment } : {} })),
    setAssessment,
  );
export const useCancelAssessment = () =>
  useMut(({ id, reason }: { id: string; reason: string }) => call(api.POST('/api/v1/people/assessments/{id}/cancel', { params: { path: { id } }, body: { reason } })), setAssessment);

// Мотивація й бонуси
export const useCreateScheme = () => useMut((body: SchemeCreate) => call(api.POST('/api/v1/people/bonus-schemes', { body })));
export const usePatchScheme = () =>
  useMut(({ id, body }: { id: string; body: SchemePatch }) => call(api.PATCH('/api/v1/people/bonus-schemes/{id}', { params: { path: { id } }, body })));
/** Пробний розрахунок без збереження (кеш не змінюється). */
export function usePreviewScheme() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PreviewBody }) => call(api.POST('/api/v1/people/bonus-schemes/{id}/preview', { params: { path: { id } }, body })),
  });
}
export const useCreateParam = () => useMut((body: ParamCreate) => call(api.POST('/api/v1/people/bonus-params', { body })));
export const usePatchParam = () => useMut(({ id, body }: { id: string; body: ParamPatch }) => call(api.PATCH('/api/v1/people/bonus-params/{id}', { params: { path: { id } }, body })));
export const useArchiveParam = () => useMut((id: string) => call(api.DELETE('/api/v1/people/bonus-params/{id}', { params: { path: { id } } })));
const setCalc = (qc: QueryClient, c: BonusCalculation) => qc.setQueryData(peopleKeys.calc(c.id), c);
export const useCalculateBonus = () => useMut((body: CalcCreate) => call(api.POST('/api/v1/people/bonus-calculations', { body })), setCalc);
export const useApproveCalc = () =>
  useMut(
    ({ id, comment }: { id: string; comment?: string }) => call(api.POST('/api/v1/people/bonus-calculations/{id}/approve', { params: { path: { id } }, body: comment ? { comment } : {} })),
    setCalc,
  );
export const useRejectCalc = () =>
  useMut(({ id, comment }: { id: string; comment: string }) => call(api.POST('/api/v1/people/bonus-calculations/{id}/reject', { params: { path: { id } }, body: { comment } })), setCalc);

// Завершення співпраці
const setOffboarding = (qc: QueryClient, o: Offboarding) => qc.setQueryData(peopleKeys.offboarding(o.id), o);
export const useStartOffboarding = () => useMut((body: OffboardingCreate) => call(api.POST('/api/v1/people/offboardings', { body })), setOffboarding);
export const useTransferTasks = () =>
  useMut(
    ({ id, toUserId, reason }: { id: string; toUserId?: string; reason?: string }) =>
      call(api.POST('/api/v1/people/offboardings/{id}/transfer-tasks', { params: { path: { id } }, body: { ...(toUserId ? { toUserId } : {}), ...(reason ? { reason } : {}) } })),
    (qc, o) => {
      setOffboarding(qc, o);
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  );
export const useConfirmItem = () =>
  useMut(({ id, body }: { id: string; body: ConfirmItemBody }) => call(api.POST('/api/v1/people/offboardings/{id}/confirm', { params: { path: { id } }, body })), setOffboarding);
export const useCompleteOffboarding = () =>
  useMut(
    ({ id, note }: { id: string; note?: string }) => call(api.POST('/api/v1/people/offboardings/{id}/complete', { params: { path: { id } }, body: note ? { note } : {} })),
    (qc, o) => {
      setOffboarding(qc, o);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  );
export const useCancelOffboarding = () =>
  useMut(({ id, reason }: { id: string; reason: string }) => call(api.POST('/api/v1/people/offboardings/{id}/cancel', { params: { path: { id } }, body: { reason } })), setOffboarding);

// Погодження відсутностей
const setRoute = (qc: QueryClient, r: AbsenceRoute) => qc.setQueryData(peopleKeys.absenceRoute(r.id), r);
export const useDecideAbsence = () =>
  useMut(({ id, body }: { id: string; body: AbsenceDecideBody }) => call(api.POST('/api/v1/people/absence-routes/{id}/decide', { params: { path: { id } }, body })), (qc, r) => {
    setRoute(qc, r);
    void qc.invalidateQueries({ queryKey: ['time'] });
  });
export const useRegisterOrder = () =>
  useMut(({ id, body }: { id: string; body: AbsenceOrderBody }) => call(api.POST('/api/v1/people/absence-routes/{id}/order', { params: { path: { id } }, body })), (qc, r) => {
    setRoute(qc, r);
    void qc.invalidateQueries({ queryKey: ['time'] });
  });
